import React, { useEffect, useState } from "react";
import { useSearchParams, useNavigate, useLocation } from "react-router-dom";
import "./PaymentReturn.css";
import Footer from "../../Component/Footer/Footer";
import NavigationBar from "../../Component/NavigationBar/NavigationBar";

const PaymentReturn = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  const [status, setStatus] = useState("processing");
  const [message, setMessage] = useState("Verifying your payment...");
  const [bookingId, setBookingId] = useState(null);
  const [retryCount, setRetryCount] = useState(0);
  const [hasProcessed, setHasProcessed] = useState(false);
  const MAX_RETRIES = 3;

  // Payment verification
  useEffect(() => {
    const verifyPayment = async () => {
      if (hasProcessed && status === "success") {
        return;
      }

      try {
        // ✅ STEP 1: Get the FULL order_id from URL
        let fullOrderId =
          searchParams.get("order_id") ||
          searchParams.get("orderId") ||
          searchParams.get("cf_order_id") ||
          searchParams.get("merchant_order_id");

        if (!fullOrderId && location.hash) {
          const hashParams = new URLSearchParams(location.hash.substring(1));
          fullOrderId =
            hashParams.get("order_id") ||
            hashParams.get("orderId") ||
            hashParams.get("cf_order_id");
        }

        if (!fullOrderId && location.pathname) {
          const pathMatch = location.pathname.match(/\/payment-return\/(.+)/);
          if (pathMatch) {
            fullOrderId = pathMatch[1];
          }
        }

        if (!fullOrderId) {
          setStatus("error");
          setMessage("Payment reference not found in URL.");
          return;
        }

        console.log("🔍 Full order ID from URL:", fullOrderId);

        // ✅ STEP 2: Extract booking ID (just the number)
        let extractedBookingId = null;
        
        // Handle formats: "booking-243" or "booking-243-retry-123456"
        if (fullOrderId.startsWith("booking-")) {
          const orderIdWithoutPrefix = fullOrderId.substring("booking-".length);
          const match = orderIdWithoutPrefix.match(/^(\d+)/);
          if (match) {
            extractedBookingId = match[1];
          }
        }

        if (!extractedBookingId) {
          setStatus("error");
          setMessage(`Invalid booking reference format: ${fullOrderId}`);
          return;
        }

        setBookingId(extractedBookingId);
        console.log("📋 Extracted booking ID:", extractedBookingId);

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 30000);

        try {
          // ✅ STEP 3: Call backend with booking_id AND full order_id
          const response = await fetch(
            `http://server.theartkothi.com/api/v1/payments/cashfree/order-status/${extractedBookingId}?order_id=${encodeURIComponent(fullOrderId)}`,
            {
              method: "GET",
              headers: {
                "Content-Type": "application/json",
                Accept: "application/json",
              },
              signal: controller.signal,
            }
          );

          clearTimeout(timeoutId);

          if (!response.ok) {
            throw new Error(`Server returned ${response.status}`);
          }

          const result = await response.json();

          console.log("📦 Payment verification - Full API response:", result);

          // Check the actual response structure
          const isSuccess = result.status === true;
          const bookingStatus = result.result?.booking_status;
          const resultMessage = result.message || "";

          console.log("📊 Status analysis:", {
            isSuccess,
            bookingStatus,
            resultMessage,
            fullOrderId,
            extractedBookingId
          });

          if (isSuccess || bookingStatus === "CONFIRMED") {
            setStatus("success");
            setMessage(resultMessage || "Payment successful! Your booking is confirmed.");
            setHasProcessed(true);
          } else if (resultMessage.includes("pending") || resultMessage.includes("processing") || resultMessage.includes("ACTIVE")) {
            setStatus("pending");
            setMessage("Payment is being processed. Please wait...");

            if (retryCount < MAX_RETRIES) {
              setTimeout(() => {
                setRetryCount((prev) => prev + 1);
              }, 5000);
            } else {
              setStatus("error");
              setMessage("Payment is taking longer than expected. Please check your booking details.");
            }
          } else {
            setStatus("error");
            setMessage(resultMessage || "Payment verification failed.");
          }
        } catch (fetchError) {
          clearTimeout(timeoutId);
          throw fetchError;
        }
      } catch (error) {
        console.error("Payment verification error:", error);
        setStatus("error");
        setMessage("Unable to verify payment. Please contact support.");
      }
    };

    if (retryCount <= MAX_RETRIES && !hasProcessed) {
      verifyPayment();
    }
  }, [searchParams, navigate, location, retryCount, hasProcessed, status]);

  const handleShowBookingSummary = () => {
    if (bookingId) {
      navigate(`/booking/${bookingId}`);
    }
  };

  const handleViewMyBookings = () => {
    if (bookingId) {
      navigate(`/booking/${bookingId}`);
    } else {
      navigate("/bookings");
    }
  };

  return (
    <div>
      <NavigationBar />
      <div className="payment-return-container">
        <div className="status-card">
          {/* Status display */}
          {status === "processing" && (
            <>
              <div className="spinner">⏳</div>
              <h2>Processing Payment</h2>
              <p>{message}</p>
              {retryCount > 0 && (
                <p className="retry-info">
                  Retry attempt {retryCount}/{MAX_RETRIES}
                </p>
              )}
            </>
          )}

          {status === "success" && (
            <>
              <div className="success-icon">✅</div>
              <h2>Booking Confirmed!</h2>
              <p>{message}</p>
              {bookingId && <p className="booking-id">Booking #{bookingId}</p>}
              
              <div className="button-group success-buttons">
                <button onClick={handleShowBookingSummary} className="primary">
                  <i className="fas fa-receipt me-2"></i>
                  View Booking Summary
                </button>
              </div>
            </>
          )}

          {status === "pending" && (
            <>
              <div className="pending-icon">⏱️</div>
              <h2>Payment Pending</h2>
              <p>{message}</p>
              {bookingId && <p className="booking-id">Booking #{bookingId}</p>}
              {retryCount < MAX_RETRIES && (
                <p className="retry-msg">
                  Checking again in 5 seconds... ({retryCount + 1}/{MAX_RETRIES})
                </p>
              )}
            </>
          )}

          {status === "error" && (
            <>
              <div className="error-icon">❌</div>
              <h2>Payment Verification Failed</h2>
              <p>{message}</p>
              {bookingId && (
                <p className="booking-id">Booking Reference: #{bookingId}</p>
              )}
              <div className="button-group">
                <button onClick={handleViewMyBookings} className="primary">
                  View My Booking
                </button>
                <button
                  onClick={() => navigate("/contact")}
                  className="secondary"
                >
                  Contact Support
                </button>
              </div>
            </>
          )}
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PaymentReturn;